import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';
import { FaTruck, FaTimes, FaCheck, FaBuilding, FaIdCard, FaPhone, FaMapMarkerAlt, FaFileInvoice } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './EditPurchaseSupplierModal.css';

/**
 * EditPurchaseSupplierModal Component
 * 
 * Allows changing the supplier on a Purchase Order.
 * Only the Supplier Name can be selected / edited.
 * All remaining fields (GST, DL1, DL2, Phone, Address) are strictly auto-populated
 * from the supplier's master record in the database.
 */
const EditPurchaseSupplierModal = ({
  isOpen,
  onClose,
  onSave,
  currentSupplier = null,
  suppliersList = []
}) => {
  const [suppliers, setSuppliers] = useState(suppliersList || []);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [selectedSupplierObj, setSelectedSupplierObj] = useState(null);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);

  // Fetch all suppliers from backend if list is empty
  useEffect(() => {
    if (isOpen) {
      if (!suppliers || suppliers.length === 0) {
        setIsLoadingSuppliers(true);
        axios
          .get(ENDPOINTS.GET_ALL_SUPPLIERS, { params: { size: 500 } })
          .then((res) => {
            const list = Array.isArray(res.data) ? res.data : res.data?.content || [];
            setSuppliers(list);
          })
          .catch((err) => console.warn('Could not fetch suppliers:', err))
          .finally(() => setIsLoadingSuppliers(false));
      }
    }
  }, [isOpen]);

  // Sync initial supplier when modal opens
  useEffect(() => {
    if (isOpen) {
      const activeName = currentSupplier?.name || currentSupplier?.supplierName || '';
      const activeId = currentSupplier?.id || currentSupplier?.supplierId || '';

      setSupplierName(activeName);
      if (activeId) {
        setSelectedSupplierId(String(activeId));
      }

      // Find matching supplier
      const match = suppliers.find(
        (s) =>
          (activeId && String(s.id) === String(activeId)) ||
          (activeName && String(s.name || '').trim().toLowerCase() === activeName.trim().toLowerCase())
      );

      if (match) {
        setSelectedSupplierObj(match);
        setSelectedSupplierId(String(match.id));
        setSupplierName(match.name);
      } else if (currentSupplier && (currentSupplier.dl1Number || currentSupplier.gstNumber)) {
        setSelectedSupplierObj(currentSupplier);
      } else {
        setSelectedSupplierObj(null);
      }
    }
  }, [isOpen, currentSupplier, suppliers]);

  if (!isOpen) return null;

  const handleSelectSupplier = (e) => {
    const suppId = e.target.value;
    setSelectedSupplierId(suppId);

    const found = suppliers.find((s) => String(s.id) === String(suppId));
    if (found) {
      setSelectedSupplierObj(found);
      setSupplierName(found.name);
    } else {
      setSelectedSupplierObj(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!supplierName.trim()) {
      toast.warning('Please select or enter a supplier name.');
      return;
    }

    const payload = selectedSupplierObj
      ? { ...selectedSupplierObj, name: supplierName.trim() }
      : {
          id: selectedSupplierId || null,
          name: supplierName.trim()
        };

    onSave(payload);
  };

  return (
    <div className="vt-eps-modal-backdrop" onClick={onClose}>
      <div className="vt-eps-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="vt-eps-modal-header">
          <div className="vt-eps-title-group">
            <div className="vt-eps-icon-box">
              <FaTruck size={18} />
            </div>
            <div>
              <h3 className="vt-eps-title">Edit Supplier</h3>
              <p className="vt-eps-subtitle">
                Select supplier name. All license, GST, and contact details will be auto-populated.
              </p>
            </div>
          </div>
          <button type="button" className="vt-eps-close-btn" onClick={onClose}>
            <FaTimes size={14} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="vt-eps-modal-body">
          {/* Supplier Name Selection (Only Editable Field) */}
          <div className="vt-eps-form-group highlight-group">
            <div className="vt-eps-label-row">
              <label htmlFor="supplierNameSelect" className="vt-eps-label">
                <FaBuilding className="field-icon" /> Supplier Name <span className="req-star">*</span>
              </label>
              <span className="vt-eps-editable-pill">Editable Field</span>
            </div>

            <select
              id="supplierNameSelect"
              className="vt-eps-select"
              value={selectedSupplierId}
              onChange={handleSelectSupplier}
              disabled={isLoadingSuppliers}
            >
              <option value="">-- Choose Existing Supplier --</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.phone1 ? `(${s.phone1})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Auto-Populated Information Notice */}
          <div className="vt-eps-notice-banner">
            <span>The following details are automatically populated from the selected supplier's profile:</span>
          </div>

          {/* Auto-Populated Grid */}
          <div className="vt-eps-auto-grid">
            {/* GST Number */}
            <div className="vt-eps-form-group">
              <div className="vt-eps-label-row">
                <label className="vt-eps-label auto-lbl">
                  <FaFileInvoice className="field-icon" /> GST Number
                </label>
                <span className="vt-eps-auto-pill">Auto populated</span>
              </div>
              <input
                type="text"
                className="vt-eps-input readonly"
                readOnly
                disabled
                value={selectedSupplierObj?.gstNumber || 'N/A'}
              />
            </div>

            {/* DL No 1 */}
            <div className="vt-eps-form-group">
              <div className="vt-eps-label-row">
                <label className="vt-eps-label auto-lbl">
                  <FaIdCard className="field-icon" /> Drug License (DL No 1)
                </label>
                <span className="vt-eps-auto-pill">Auto populated</span>
              </div>
              <input
                type="text"
                className="vt-eps-input readonly"
                readOnly
                disabled
                value={selectedSupplierObj?.dl1Number || selectedSupplierObj?.dlNumber1 || 'N/A'}
              />
            </div>

            {/* DL No 2 */}
            <div className="vt-eps-form-group">
              <div className="vt-eps-label-row">
                <label className="vt-eps-label auto-lbl">
                  <FaIdCard className="field-icon" /> Drug License (DL No 2)
                </label>
                <span className="vt-eps-auto-pill">Auto populated</span>
              </div>
              <input
                type="text"
                className="vt-eps-input readonly"
                readOnly
                disabled
                value={selectedSupplierObj?.dl2Number || selectedSupplierObj?.dlNumber2 || 'N/A'}
              />
            </div>

            {/* Phone */}
            <div className="vt-eps-form-group">
              <div className="vt-eps-label-row">
                <label className="vt-eps-label auto-lbl">
                  <FaPhone className="field-icon" /> Phone Number
                </label>
                <span className="vt-eps-auto-pill">Auto populated</span>
              </div>
              <input
                type="text"
                className="vt-eps-input readonly"
                readOnly
                disabled
                value={
                  selectedSupplierObj?.phone1
                    ? (selectedSupplierObj.phone2 ? `${selectedSupplierObj.phone1}, ${selectedSupplierObj.phone2}` : selectedSupplierObj.phone1)
                    : (selectedSupplierObj?.phone || 'N/A')
                }
              />
            </div>

            {/* Address */}
            <div className="vt-eps-form-group full-width">
              <div className="vt-eps-label-row">
                <label className="vt-eps-label auto-lbl">
                  <FaMapMarkerAlt className="field-icon" /> Address
                </label>
                <span className="vt-eps-auto-pill">Auto populated</span>
              </div>
              <input
                type="text"
                className="vt-eps-input readonly"
                readOnly
                disabled
                value={selectedSupplierObj?.address || 'N/A'}
              />
            </div>
          </div>

          {/* Modal Actions */}
          <div className="vt-eps-modal-footer">
            <button type="button" className="vt-eps-cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="vt-eps-save-btn">
              <FaCheck size={12} /> Save Supplier
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPurchaseSupplierModal;
